import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaLeaf,
  FaHeart,
  FaTags,
  FaRoute,
  FaChevronLeft,
  FaChevronRight,
  FaPause,
  FaPlay,
} from "react-icons/fa";

const Hero = () => {
  const { t } = useTranslation();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [direction, setDirection] = useState(1);

  // Slider Images
  const sliderImages = [
    {
      src: "/images/hero/slide-1.webp",
      alt: t("hero.slider.slide1"),
      
    },
    {
      src: "/images/hero/slide-2.webp",
      alt: t("hero.slider.slide2"),
    },
    {
      src: "/images/hero/slide-3.webp",
      alt: t("hero.slider.slide3"),
    },
    {
      src: "/images/hero/slide-4.webp",
      alt: t("hero.slider.slide4"),
    },
    {
      src: "/images/hero/slide-5.webp",
      alt: t("hero.slider.slide5"),
    },
  ];

  const features = [
    { icon: <FaLeaf />, text: t("hero.features.organic") },
    { icon: <FaHeart />, text: t("hero.features.vegan") },
    { icon: <FaRoute />, text: t("hero.features.traceability") },
    { icon: <FaTags />, text: t("hero.features.privateLabel") },
  ];

  // Certifications data
  const certifications = [
    { name: "BRC", image: "/images/certifications/BRC.webp" },
    { name: "EU", image: "/images/certifications/EU.webp" },
    { name: "USDA", image: "/images/certifications/USDA.webp" },
    { name: "JAS", image: "/images/certifications/JAS.webp" },
    { name: "Naturland", image: "/images/certifications/Naturland.webp" },
    { name: "FairTrade", image: "/images/certifications/FairTrade.webp" },
    { name: "FDA", image: "/images/certifications/FDA.webp" },
  ];

  <div itemScope itemType="https://schema.org/Certification">
    <img src="/images/certifications/BRC.webp" alt="BRC A+ Certified" />
    <meta itemProp="name" content="BRC Global Standard A+" />
  </div>;
  // Navigation functions
  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % sliderImages.length);
  }, [sliderImages.length]);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentSlide(
      (prev) => (prev - 1 + sliderImages.length) % sliderImages.length
    );
  }, [sliderImages.length]);

  const goToSlide = useCallback(
    (index) => {
      setDirection(index > currentSlide ? 1 : -1);
      setCurrentSlide(index);
    },
    [currentSlide]
  );

  // Auto-play slider
  useEffect(() => {
    if (!isAutoPlaying) return;
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, nextSlide]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === "ArrowLeft") prevSlide();
      if (e.key === "ArrowRight") nextSlide();
    };
    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [nextSlide, prevSlide]);

  // Slide animation variants
  const slideVariants = {
    enter: {
      opacity: 0,
      scale: 1.05,
    },
    center: {
      zIndex: 1,
      opacity: 1,
      scale: 1,
    },
    exit: {
      zIndex: 0,
      opacity: 0,
      scale: 0.95,
    },
  };

  <section itemScope itemType="https://schema.org/Organization">
    <h1 itemProp="name">WORGA NATURALS</h1>
    <p itemProp="description">Certified Organic Excellence Since 2017</p>
    <meta itemProp="foundingDate" content="2013" />
  </section>;

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-primary-50 via-white to-earth-50 dark:from-dark-900 dark:via-dark-800 dark:to-dark-900 pt-32 pb-20">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
          className="absolute -top-40 -right-40 w-96 h-96 bg-primary-200/30 dark:bg-primary-900/20 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-earth-200/30 dark:bg-earth-900/20 rounded-full blur-3xl"
        />
      </div>

      <div className="container mx-auto px-6 md:px-12 lg:px-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center mb-20">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="px-4 md:px-0"
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 px-4 py-2 rounded-full text-sm font-semibold mb-6"
            >
              <FaLeaf className="text-green-600 dark:text-green-400" />
              {t("hero.badge")}
            </motion.div>

            {/* Main Heading */}
            <h1 className="text-5xl md:text-7xl font-bold font-display text-gray-900 dark:text-white mb-8 leading-tight">
              {t("hero.title.part1")}{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-primary-600 dark:from-green-400 dark:to-primary-400">
                {t("hero.title.part2")}
              </span>
            </h1>

            {/* Description with Bold Keywords */}
            <div className="space-y-5 mb-10">
              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                {t("hero.description.paragraph1.part1")}{" "}
                <span className="font-bold text-gray-900 dark:text-white">
                  {t("hero.description.paragraph1.highlight1")}
                </span>{" "}
                {t("hero.description.paragraph1.part2")}{" "}
                <span className="font-bold text-gray-900 dark:text-white">
                  {t("hero.description.paragraph1.highlight2")}
                </span>
                {t("hero.description.paragraph1.part3")}
              </p>

              <p className="text-lg md:text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
                {t("hero.description.paragraph2.part1")}{" "}
                <span className="font-bold text-gray-900 dark:text-white">
                  {t("hero.description.paragraph2.highlight1")}
                </span>
                —{" "}
                <span className="font-bold text-gray-900 dark:text-white">
                  {t("hero.description.paragraph2.highlight2")}
                </span>{" "}
                {t("hero.description.paragraph2.part2")}{" "}
                <span className="font-bold text-gray-900 dark:text-white">
                  {t("hero.description.paragraph2.highlight3")}
                </span>
                .
              </p>
            </div>

            {/* Features */}
            <div className="flex flex-wrap gap-4 mb-10">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex items-center space-x-2 bg-white dark:bg-dark-800 px-5 py-3 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300"
                >
                  <span className="text-primary-600 dark:text-primary-400 text-xl">
                    {feature.icon}
                  </span>
                  <span className="font-semibold text-gray-700 dark:text-gray-200 text-sm md:text-base">
                    {feature.text}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Right Content - Professional Image Slider */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="relative px-4 md:px-0"
          >
            <div className="relative group">
              {/* Main Slider Container */}
              <div className="relative rounded-3xl overflow-hidden shadow-2xl bg-gradient-to-br from-primary-100 to-primary-200 dark:from-primary-900/30 dark:to-primary-800/30 p-2">
                <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-gray-100 dark:bg-gray-800">
                  <AnimatePresence initial={false} custom={direction}>
                    <motion.div
                      key={currentSlide}
                      custom={direction}
                      variants={slideVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                      transition={{
                        x: { type: "spring", stiffness: 300, damping: 30 },
                        opacity: { duration: 0.3 },
                        scale: { duration: 0.3 },
                      }}
                      className="absolute inset-0"
                    >
                      <img
                        src={sliderImages[currentSlide].src}
                        alt={sliderImages[currentSlide].alt}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />

                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />

                      {/* Slide Caption */}
                      <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                        <motion.h3
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.2 }}
                          className="text-2xl font-bold mb-2"
                        >
                          {sliderImages[currentSlide].alt}
                        </motion.h3>
                      </div>
                    </motion.div>
                  </AnimatePresence>

                  {/* Navigation Arrows */}
                  <button
                    onClick={prevSlide}
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/90 dark:bg-dark-800/90 hover:bg-white dark:hover:bg-dark-700 text-gray-800 dark:text-white p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
                    aria-label="Previous slide"
                  >
                    <FaChevronLeft className="text-xl" />
                  </button>

                  <button
                    onClick={nextSlide}
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/90 dark:bg-dark-800/90 hover:bg-white dark:hover:bg-dark-700 text-gray-800 dark:text-white p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
                    aria-label="Next slide"
                  >
                    <FaChevronRight className="text-xl" />
                  </button>

                  {/* Play/Pause Button */}
                  <button
                    onClick={() => setIsAutoPlaying(!isAutoPlaying)}
                    className="absolute top-4 right-4 z-20 bg-white/90 dark:bg-dark-800/90 hover:bg-white dark:hover:bg-dark-700 text-gray-800 dark:text-white p-3 rounded-full shadow-lg opacity-0 group-hover:opacity-100 transition-all duration-300"
                    aria-label={
                      isAutoPlaying ? "Pause slideshow" : "Play slideshow"
                    }
                  >
                    {isAutoPlaying ? (
                      <FaPause className="text-lg" />
                    ) : (
                      <FaPlay className="text-lg" />
                    )}
                  </button>

                  {/* Slide Indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                    {sliderImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`transition-all duration-300 rounded-full ${
                          index === currentSlide
                            ? "w-8 h-2 bg-white"
                            : "w-2 h-2 bg-white/50 hover:bg-white/75"
                        }`}
                        aria-label={`Go to slide ${index + 1}`}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {/* Decorative Elements */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -top-6 -right-6 w-24 h-24 bg-primary-400/20 dark:bg-primary-600/20 rounded-full blur-2xl"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-6 -left-6 w-32 h-32 bg-earth-400/20 dark:bg-earth-600/20 rounded-full blur-2xl"
              />
            </div>
          </motion.div>
        </div>

        {/* Certifications Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-20"
        >
          <h3 className="text-center text-2xl md:text-3xl lg:text-4xl font-bold mb-8 leading-tight">
            <span className="text-gray-800 dark:text-white">
              {t("hero.certifications.title.part1")}
            </span>
            {"  "}
            <br className="md:hidden" />
            <span className="text-green-600 dark:text-green-400">
              {t("hero.certifications.title.part2")}
            </span>
          </h3>
          <div className="flex flex-wrap justify-center items-center gap-8">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.9 + index * 0.1 }}
                whileHover={{ scale: 1.1 }}
                className="bg-white dark:bg-dark-800 p-4 rounded-xl shadow-md hover:shadow-xl transition-all duration-300"
              >
                <img
                  src={cert.image}
                  alt={cert.name}
                  className="h-16 w-auto object-contain grayscale hover:grayscale-0 transition-all duration-300"
                />
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
